import { zodResolver } from '@hookform/resolvers/zod'
import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import Box from '@repo/ui/box'
import Button from '@repo/ui/button'
import { PlusIcon } from '@repo/ui/icons'
import InnerBox from '@repo/ui/inner-box'
import Typography from '@repo/ui/typography'
import { useMutation, useQuery } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { toast } from 'sonner'

import { getCseRegistration } from '../../../api/enrollments/add-cse-registration'
import { updateOtoIndividualProposition } from '../../../api/enrollments/update-oto-individual-proposition'
import { useModalManager } from '../../../lib/hooks/use-modal-manager'
import useTranslate from '../../../lib/hooks/use-translate'
import { GenderEnum, genderOptionsMapping } from '../../../lib/types/enums/common/gender.enum'
import { ApplicationStatusEnum } from '../../../lib/types/enums/enrollment/application-status.enum'
import { EnrollmentQuotationInput } from '../../../lib/types/models/enrollment/enrollment-input'
import {
  mapOtoIndividualPropositionInputToRequest,
  OtoIndividualPropositionInput
} from '../../../lib/types/models/enrollment-proposition/oto-individual-proposition-input'
import { BaseFormProps } from '../../../lib/types/utils'
import { getSubmitAction } from '../../../lib/utils/buttons-config/validation-buttons-config'
import { showAxiosError } from '../../../lib/utils/functions/misc.functions'
import { FORM_MODE } from '../../../utils/constants/form.constants'
import { Form, FormControl, FormField, FormItem } from '../../elements/forms/form'
import ControlledInput from '../../elements/inputs/controlled-input'
import ControlledPhoneInput from '../../elements/inputs/controlled-phone-input'
import ControlledRadioInput from '../../elements/inputs/controlled-radio-input'
import ValidationButtons from '../../elements/validation-buttons'
import EnrollmentPropositionDetails from './enrollment-form/enrollment-proposition-details'
import EnrollmentQuotationHeader from './enrollment-quotation-header'
import EnrollmentPropositionFormModal from './enrolment-create-proposition-steps/enrollment-proposition-form-modal'
import OtoIndividualPropositionFormModal from './enrolment-create-proposition-steps/oto-individual-proposition-form-modal'
import OtoIndividualPropositionSummary from './enrolment-create-proposition-steps/oto-individual-proposition-summary'
import { ENROLLMENT_QUOTATION_SCHEMA } from './utils/enrollments-utils'

type Props = BaseFormProps<EnrollmentQuotationInput> & {
  mode: 'create' | 'update'
  enrollmentId?: number
  propositionId?: number
  onJourneyChange?: () => void
}

const EnrollmentQuotationForm = ({
  mode,
  enrollmentId,
  propositionId,
  defaultValues,
  onJourneyChange,
  ...validationButtonProps
}: Props) => {
  const { t } = useTranslate()
  const form = useForm<EnrollmentQuotationInput>({
    resolver: zodResolver(ENROLLMENT_QUOTATION_SCHEMA(t)),
    defaultValues,
    mode: FORM_MODE
  })

  const { modalState, toggleModal, closeModel } = useModalManager<'create-journey' | 'create-oto'>(
    ['create-journey', 'create-oto']
  )
  const [otoModalIndex, setOtoModalIndex] = useState(0)

  const { mutateAsync: updateOtoIndividualPropositionMutation } = useMutation({
    mutationFn: updateOtoIndividualProposition,
    onError: showAxiosError,
    onSuccess: () => toast.success('Proposition One to One Individuel modifiée avec succès')
  })

  const { data: cseRegistration } = useQuery({
    queryKey: ['cse-registration', enrollmentId],
    queryFn: () => {
      if (enrollmentId) return getCseRegistration(enrollmentId)
    },
    enabled: !!enrollmentId && mode === 'update'
  })

  const { proposition, otoIndividualProposition } = form.watch()

  // in update mode the OTO Individual proposition is persisted immediately
  const handleOtoPropositionChange = async (values: OtoIndividualPropositionInput) => {
    if (mode === 'update' && enrollmentId)
      await updateOtoIndividualPropositionMutation({
        id: enrollmentId,
        ...mapOtoIndividualPropositionInputToRequest(values)
      })
    form.setValue('otoIndividualProposition', values)
    form.clearErrors('proposition')
  }

  const journey = proposition?.journey

  useEffect(() => {
    if (journey) onJourneyChange?.()
  }, [journey])

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(getSubmitAction(validationButtonProps.buttonsConfig))}>
        <Box className="p-5 flex flex-col gap-6">
          {mode === 'update' && enrollmentId && (
            <EnrollmentQuotationHeader enrollmentId={enrollmentId} />
          )}
          <InnerBox className="p-5 rounded-2xl">
            <div className="flex items-center gap-2">
              <Typography variant="text" color={tailwindColors.black.DEFAULT}>
                Séjour
              </Typography>
              <Typography.Link
                variant="label-semibold"
                color={tailwindColors.blue.DEFAULT}
                onClick={() => {
                  window.open('https://www.sejour-linguistique-lec.fr/', '_blank')
                }}
              >
                Accéder au site
              </Typography.Link>
            </div>

            <div className=" flex gap-3 flex-row mt-5">
              {!proposition && !otoIndividualProposition && (
                <Button
                  type="button"
                  variant={form.formState.errors.proposition ? 'destructive' : 'secondary'}
                  icon={PlusIcon}
                  label="Ajouter un séjour"
                  className="w-fit"
                  onClick={() => toggleModal('create-journey')}
                />
              )}

              {!proposition && !otoIndividualProposition && (
                <Button
                  type="button"
                  variant={form.formState.errors.proposition ? 'destructive' : 'secondary'}
                  icon={PlusIcon}
                  label="Ajouter OTO Indiv"
                  className="w-fit"
                  onClick={() => {
                    setOtoModalIndex(0)
                    toggleModal('create-oto')
                  }}
                />
              )}
            </div>

            {otoIndividualProposition && (
              <OtoIndividualPropositionSummary
                input={otoIndividualProposition}
                onEdit={(tabIndex) => {
                  setOtoModalIndex(tabIndex)
                  toggleModal('create-oto')
                }}
                onInputChange={handleOtoPropositionChange}
              />
            )}

            {proposition && mode === 'create' && (
              <EnrollmentPropositionDetails
                mode={'normal'}
                status={ApplicationStatusEnum.QUOTE}
                input={proposition}
                onPropositionChange={(proposition) => form.setValue('proposition', proposition)}
              />
            )}

            {proposition && mode === 'update' && enrollmentId && propositionId && (
              <EnrollmentPropositionDetails
                mode={'instant'}
                status={ApplicationStatusEnum.QUOTE}
                enrollmentId={enrollmentId}
                propositionId={propositionId}
              />
            )}
          </InnerBox>
          <InnerBox
            title="Informations "
            titleColor={tailwindColors.black.DEFAULT}
            className="p-5 rounded-2xl"
          >
            <Typography variant="caption" color={tailwindColors.black.DEFAULT}>
              Enfant
            </Typography>
            <div className="h-2" />
            <div className="flex gap-2">
              <FormField
                control={form.control}
                name="childLastName"
                render={({ field }) => (
                  <FormItem className="flex-1">
                    <FormControl>
                      <ControlledInput {...field} required label="NOM" type="text" />
                    </FormControl>
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="childFirstName"
                render={({ field }) => (
                  <FormItem className="flex-1">
                    <FormControl>
                      <ControlledInput {...field} required label="Prénom" type="text" />
                    </FormControl>
                  </FormItem>
                )}
              />
            </div>
            <div className="h-3" />
            <Typography variant="caption" color={tailwindColors.black.DEFAULT}>
              Parent 1 *
            </Typography>
            <div className="h-2" />
            <div className="flex gap-20">
              {Object.values(GenderEnum).map((gender) => (
                <FormField
                  key={gender}
                  control={form.control}
                  name="parent1Gender"
                  render={({ field }) => (
                    <FormItem>
                      <FormControl>
                        <ControlledRadioInput
                          {...field}
                          required
                          label={genderOptionsMapping[gender].title}
                          checked={field.value === gender}
                          onCheck={() => field.onChange(gender)}
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />
              ))}
            </div>
            <div className="h-2" />
            <div className="flex gap-2">
              <FormField
                control={form.control}
                name="parent1LastName"
                render={({ field }) => (
                  <FormItem className="flex-1">
                    <FormControl>
                      <ControlledInput {...field} required label="NOM" type="text" />
                    </FormControl>
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="parent1FirstName"
                render={({ field }) => (
                  <FormItem className="flex-1">
                    <FormControl>
                      <ControlledInput {...field} label="Prénom" type="text" />
                    </FormControl>
                  </FormItem>
                )}
              />
            </div>
            <div className="h-3" />
            <FormField
              control={form.control}
              name="parent1Email"
              render={({ field }) => (
                <FormItem className="flex-1">
                  <FormControl>
                    <ControlledInput {...field} required label="Email" type="email" />
                  </FormControl>
                </FormItem>
              )}
            />
            <div className="h-3" />
            <FormField
              control={form.control}
              name="parent1PhoneNumber"
              render={({ field }) => (
                <FormItem className="flex-1">
                  <FormControl>
                    <ControlledPhoneInput
                      placeholder="Téléphone"
                      label="Téléphone"
                      {...field}
                      required
                      defaultCountry="FR"
                    />
                  </FormControl>
                </FormItem>
              )}
            />
          </InnerBox>
          <ValidationButtons {...validationButtonProps} />
        </Box>
        <OtoIndividualPropositionFormModal
          isOpen={modalState['create-oto']}
          defaultIndex={otoModalIndex}
          defaultValues={form.watch('otoIndividualProposition')}
          onClose={() => closeModel('create-oto')}
          handleSubmit={async (values) => {
            await handleOtoPropositionChange(values)
            closeModel('create-oto')
          }}
        />
        <EnrollmentPropositionFormModal
          isOpen={modalState['create-journey']}
          onClose={() => closeModel('create-journey')}
          defaultValues={{
            ...form.watch('proposition'),
            cseCode: form.watch('proposition.cseCode') ?? cseRegistration?.cse?.code
          }}
          handleSubmit={(values) => {
            form.setValue('proposition', values)
            closeModel('create-journey')
          }}
        />
      </form>
    </Form>
  )
}

export default EnrollmentQuotationForm
